import React, { useState, useEffect } from 'react';
import { 
  Users, 
  MessageCircle, 
  Heart, 
  Share2, 
  MoreHorizontal, 
  Image, 
  Send, 
  MapPin, 
  Calendar, 
  Bookmark, 
  Camera, 
  X, 
  Plus, 
  Search, 
  UserPlus, 
  Check, 
  Grid3X3, 
  Play, 
  ChevronLeft, 
  ChevronRight, 
  MessageSquare, 
  Compass, 
  Star, 
  Route, 
  Utensils, 
  Mountain, 
  Landmark, 
  Lightbulb, 
  CheckCircle2, 
  TrendingUp, 
  Award, 
  Navigation, 
  UserCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { communityApi, placesApi, messagesApi } from '../services/api';

const CommunityPage = ({ onTabChange }) => {
  const { isAuthenticated, user } = useAuth();
  const { darkMode } = useTheme();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('feed'); // feed, discover
  const [posts, setPosts] = useState([]);
  const [stories, setStories] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  const [trendingPlaces, setTrendingPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitedPosts, setVisitedPosts] = useState([]);
  const [savedPosts, setSavedPosts] = useState([]);
  const [followingUsers, setFollowingUsers] = useState([]);
  
  // Post creation
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [newPost, setNewPost] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [postRating, setPostRating] = useState(0);
  const [postImage, setPostImage] = useState(null);
  const [postImagePreview, setPostImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState([]);
  const [showUserTagModal, setShowUserTagModal] = useState(false);
  const [userTagSearch, setUserTagSearch] = useState('');
  const [userTagResults, setUserTagResults] = useState([]);
  const fileInputRef = React.useRef(null);
  const cameraInputRef = React.useRef(null);
  
  // Comments
  const [showComments, setShowComments] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  
  // User Profile Modal
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserPosts, setSelectedUserPosts] = useState([]);
  const [selectedUserStats, setSelectedUserStats] = useState({ posts: 0, followers: 0, following: 0 });
  const [isFollowing, setIsFollowing] = useState(false);
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  const [selectedUserPost, setSelectedUserPost] = useState(null); // For post detail view
  const [currentUserPostIndex, setCurrentUserPostIndex] = useState(0);
  const [userProfileFilter, setUserProfileFilter] = useState('all'); // Filter for user profile posts
  
  // Message Modal
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageRecipient, setMessageRecipient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);

  // User Search
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Post Categories
  const categories = [
    { id: 'visit', icon: MapPin, label: 'Mekan Ziyareti', color: 'blue' },
    { id: 'food', icon: Utensils, label: 'Yemek Deneyimi', color: 'orange' },
    { id: 'nature', icon: Mountain, label: 'Doğa Keşfi', color: 'green' },
    { id: 'photo', icon: Camera, label: 'Fotoğraf Noktası', color: 'purple' },
    { id: 'tip', icon: Lightbulb, label: 'Yerel İpucu', color: 'yellow' },
  ];

  // Get filtered posts for user profile
  const getFilteredUserPosts = () => {
    const safePosts = Array.isArray(selectedUserPosts) ? selectedUserPosts : [];
    if (userProfileFilter === 'all') return safePosts;
    return safePosts.filter(post => post.category === userProfileFilter);
  };

  // Dark mode classes
  const bgMain = darkMode ? 'bg-slate-900' : 'bg-[#f5f7fa]';
  const bgCard = darkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = darkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = darkMode ? 'text-slate-400' : 'text-gray-500';
  const borderColor = darkMode ? 'border-slate-700' : 'border-gray-200';

  // Local guides state
  const [localGuides, setLocalGuides] = useState([]);

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      
      // Fetch posts from API - Defensive
      const postsData = await communityApi.getPosts();
      setPosts(Array.isArray(postsData) ? postsData : []);
      
      // Fetch trending places - Defensive
      const trendingData = await communityApi.getTrending();
      setTrendingPlaces(Array.isArray(trendingData) ? trendingData : []);
      
      // Fetch local guides - Defensive
      const guidesData = await communityApi.getLocalGuides();
      setLocalGuides(Array.isArray(guidesData) ? guidesData : []);
      
      // Fetch following list to persist follow state - Defensive
      if (isAuthenticated) {
        try {
          const followingList = await communityApi.getFollowingList();
          setFollowingUsers(Array.isArray(followingList) ? followingList : []);
        } catch (err) {
          console.error('Error fetching following list:', err);
          setFollowingUsers([]);
        }
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setPosts([]);
      setTrendingPlaces([]);
      setLocalGuides([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter posts by category
  const [activeCategory, setActiveCategory] = useState('all');
  
  const safePosts = Array.isArray(posts) ? posts : [];
  const filteredPosts = activeCategory === 'all' 
    ? safePosts 
    : safePosts.filter(p => p.category === activeCategory);

  const toggleVisited = async (postId) => {
    if (!isAuthenticated) return;
    
    const isVisited = visitedPosts.includes(postId);
    
    // Optimistic update
    if (isVisited) {
      setVisitedPosts(prev => prev.filter(id => id !== postId));
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, visited: post.visited - 1 } : post
      ));
    } else {
      setVisitedPosts(prev => [...prev, postId]);
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, visited: post.visited + 1 } : post
      ));
    }
    
    try {
      await communityApi.toggleVisited(postId);
    } catch (error) {
      // Revert on error
      if (isVisited) {
        setVisitedPosts(prev => [...prev, postId]);
      } else {
        setVisitedPosts(prev => prev.filter(id => id !== postId));
      }
    }
  };

  const toggleSave = async (postId) => {
    if (!isAuthenticated) return;
    
    const isSaved = savedPosts.includes(postId);
    
    // Optimistic update
    setSavedPosts(prev => 
      isSaved 
        ? prev.filter(id => id !== postId)
        : [...prev, postId]
    );
    
    setPosts(prev => prev.map(post => 
      post.id === postId 
        ? { ...post, saves: isSaved ? post.saves - 1 : post.saves + 1 } 
        : post
    ));
    
    try {
      await communityApi.toggleSave(postId);
    } catch (error) {
      // Revert on error
      setSavedPosts(prev => 
        isSaved 
          ? [...prev, postId]
          : prev.filter(id => id !== postId)
      );
    }
  };

  const handleFollow = async (userId) => {
    if (!isAuthenticated) return;
    
    const isFollowing = followingUsers.includes(userId);
    
    // Optimistic update
    setFollowingUsers(prev => 
      isFollowing 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
    
    try {
      await communityApi.toggleFollow(userId);
    } catch (error) {
      // Revert on error
      setFollowingUsers(prev => 
        isFollowing 
          ? [...prev, userId]
          : prev.filter(id => id !== userId)
      );
    }
  };

  // Open message modal
  const openMessageModal = async (userId, userName) => {
    if (!isAuthenticated) {
      alert('Mesaj göndermek için giriş yapmalısınız');
      return;
    }
    setMessageRecipient({ id: userId, name: userName });
    setShowMessageModal(true);
    setMessages([]);
    setNewMessage('');
    
    // Fetch existing messages
    try {
      const existingMessages = await messagesApi.getMessages(userId);
      setMessages(Array.isArray(existingMessages) ? existingMessages : []);
    } catch (error) {
      console.error('Mesajlar yüklenemedi:', error);
      setMessages([]);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !messageRecipient || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const result = await messagesApi.sendMessage(messageRecipient.id, newMessage.trim());
      if (result.success && result.message) {
        setMessages(prev => [...prev, result.message]);
        setNewMessage('');
      }
    } catch (error) {
      console.error('Mesaj gönderilemedi:', error);
      alert('Mesaj gönderilemedi');
    } finally {
      setSendingMessage(false);
    }
  };

  // Search users
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const results = await communityApi.searchUsers(query);
      setSearchResults(Array.isArray(results) ? results : []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Open user profile modal
  const openUserProfile = async (authorId, authorName) => {
    setUserProfileLoading(true);
    setSelectedUser({ id: authorId, name: authorName });
    setUserProfileFilter('all'); // Reset filter when opening profile
    
    try {
      // Get user profile from backend
      const userProfile = await communityApi.getUserProfile(authorId);
      if (userProfile) {
        setSelectedUser({
          id: authorId,
          name: userProfile.name || authorName,
          username: userProfile.username,
          bio: userProfile.bio,
          avatar: userProfile.avatar,
          coverImage: userProfile.coverImage
        });
        setSelectedUserStats({
          posts: userProfile.posts || 0,
          followers: userProfile.followers || 0,
          following: userProfile.following || 0
        });
      }
      
      // Get user's posts
      const userPosts = await communityApi.getUserPosts(authorId);
      setSelectedUserPosts(Array.isArray(userPosts) ? userPosts : []);
      
      // Check if following - use backend data or local state
      setIsFollowing(userProfile.is_following || followingUsers.includes(authorId));
    } catch (error) {
      console.error('Error loading user profile:', error);
      // Fallback to old method if needed
      setSelectedUserPosts([]);
      setSelectedUserStats({ posts: 0, followers: 0, following: 0 });
    } finally {
      setUserProfileLoading(false);
    }
  };

  // Toggle follow from profile modal
  const handleProfileFollow = async () => {
    if (!selectedUser) return;
    
    const wasFollowing = isFollowing;
    setIsFollowing(!isFollowing);
    setSelectedUserStats(prev => ({
      ...prev,
      followers: wasFollowing ? prev.followers - 1 : prev.followers + 1
    }));
    
    try {
      await communityApi.toggleFollow(selectedUser.id);
      // Update main following list
      setFollowingUsers(prev => 
        wasFollowing 
          ? prev.filter(id => id !== selectedUser.id)
          : [...prev, selectedUser.id]
      );
    } catch (error) {
      // Revert on error
      setIsFollowing(wasFollowing);
      setSelectedUserStats(prev => ({
        ...prev,
        followers: wasFollowing ? prev.followers + 1 : prev.followers - 1
      }));
    }
  };

  const handleAddToRoute = (post) => {
    // Navigate to trips page with place info
    if (onTabChange) {
      onTabChange('mytrips');
      // Store selected place in sessionStorage for the trips page to pick up
      sessionStorage.setItem('addPlaceToRoute', JSON.stringify({
        id: post.placeId,
        name: post.placeName,
        fromCommunity: true
      }));
    }
  };

  const handleCreatePost = async () => {
    if (!newPost.trim() || !selectedCategory) return;
    
    try {
      setIsUploading(true);
      
      // If there's an image, convert to base64 for storage
      let imageUrl = null;
      if (postImage) {
        imageUrl = postImagePreview;
      }
      
      // Extract user IDs from tagged users
      const taggedUserIds = taggedUsers.map(user => user.id);
      
      const result = await communityApi.createPost(
        newPost, 
        selectedPlace?.id, 
        imageUrl, 
        selectedCategory, 
        postRating,
        taggedUserIds
      );
      
      // Add to posts list
      const newPostItem = {
        ...result,
        image: imageUrl,
        authorStats: { visited: 0, routes: 0 },
        visited: 0,
        comments: 0,
        saves: 0,
      };
      setPosts([newPostItem, ...posts]);
      
      // Reset form
      setNewPost('');
      setSelectedCategory(null);
      setSelectedPlace(null);
      setPostRating(0);
      setPostImage(null);
      setPostImagePreview(null);
      setTaggedUsers([]);
      setShowNewPostModal(false);
    } catch (error) {
      console.error('Post creation error:', error);
      alert('Paylaşım oluşturulamadı, lütfen tekrar deneyin');
    } finally {
      setIsUploading(false);
    }
  };

  // Handle file selection for gallery
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        alert('Dosya boyutu 5MB\'dan küçük olmalıdır');
        return;
      }
      if (!file.type.startsWith('image/')) {
        alert('Lütfen bir resim dosyası seçin');
        return;
      }
      setPostImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPostImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle camera capture
  const handleCameraCapture = (event) => {
    const file = event.target.files[0];
    if (file) {
      setPostImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPostImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Remove selected image
  const removeImage = () => {
    setPostImage(null);
    setPostImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  // Close post modal and reset form
  const closePostModal = () => {
    setShowNewPostModal(false);
    setNewPost('');
    setSelectedCategory(null);
    setSelectedPlace(null);
    setPostRating(0);
    setTaggedUsers([]);
    removeImage();
  };

  // User tagging functions
  const handleUserTagSearch = async (query) => {
    setUserTagSearch(query);
    if (!query.trim()) {
      setUserTagResults([]);
      return;
    }

    try {
      const results = await communityApi.searchUsers(query);
      // Filter out already tagged users
      const safeResults = Array.isArray(results) ? results : [];
      const filtered = safeResults.filter(r => !taggedUsers.some(t => t.id === r.id));
      setUserTagResults(filtered);
    } catch (error) {
      console.error('Kullanıcı arama hatası:', error);
      setUserTagResults([]);
    }
  };

  const addTaggedUser = (user) => {
    if (!taggedUsers.some(t => t.id === user.id)) {
      setTaggedUsers([...taggedUsers, user]);
    }
    setUserTagSearch('');
    setUserTagResults([]);
    setShowUserTagModal(false);
  };

  const removeTaggedUser = (userId) => {
    setTaggedUsers(taggedUsers.filter(u => u.id !== userId));
  };

  const getCategoryStyle = (categoryId) => {
    const styles = {
      visit: { bg: darkMode ? 'bg-blue-900/40' : 'bg-blue-100', text: 'text-blue-500', border: 'border-blue-500' },
      food: { bg: darkMode ? 'bg-orange-900/40' : 'bg-orange-100', text: 'text-orange-500', border: 'border-orange-500' },
      nature: { bg: darkMode ? 'bg-green-900/40' : 'bg-green-100', text: 'text-green-500', border: 'border-green-500' },
      photo: { bg: darkMode ? 'bg-purple-900/40' : 'bg-purple-100', text: 'text-purple-500', border: 'border-purple-500' },
      tip: { bg: darkMode ? 'bg-yellow-900/40' : 'bg-yellow-100', text: 'text-yellow-500', border: 'border-yellow-500' },
    };
    return styles[categoryId] || styles.visit;
  };

  const getCategoryIcon = (categoryId) => {
    const icons = { visit: MapPin, food: Utensils, nature: Mountain, photo: Camera, tip: Lightbulb };
    return icons[categoryId] || MapPin;
  };

  const openComments = async (postId) => {
    setShowComments(postId);
    try {
      const data = await communityApi.getComments(postId);
      setComments(Array.isArray(data) ? data : []);
    } catch {
      setComments([
        { id: 'c1', authorName: 'Ali Y.', content: 'Harika bir öneri! Hemen rotama ekledim 🗺️', timestamp: '1s önce' },
        { id: 'c2', authorName: 'Fatma Ö.', content: 'Ben de geçen hafta gittim, kesinlikle katılıyorum!', timestamp: '30dk önce' },
      ]);
    }
  };
  
  const handleAddComment = async () => {
    if (!newComment.trim() || !showComments) return;
    
    const comment = {
      id: Date.now().toString(),
      authorName: user?.name || 'Sen',
      content: newComment,
      timestamp: 'Az önce'
    };
    
    setComments([...comments, comment]);
    setNewComment('');
    
    // Update post comment count
    setPosts(prev => prev.map(post => 
      post.id === showComments ? { ...post, comments: post.comments + 1 } : post
    ));
    
    try {
      await communityApi.addComment(showComments, newComment);
    } catch (error) {
      console.log('Comment added locally');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className={`h-full ${bgMain} flex items-center justify-center p-4`}>
        <div className="text-center">
          <div className={`w-20 h-20 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <Compass className="w-10 h-10 text-[#1e3a5f]" />
          </div>
          <h2 className={`text-xl font-bold ${textPrimary} mb-2`}>Gezginlere Katıl</h2>
          <p className={`${textSecondary} mb-6`}>Keşiflerini paylaş, yerel ipuçları öğren</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-[#1e3a5f] text-white rounded-xl font-medium hover:bg-[#2a4a73] transition-colors"
          >
            Giriş Yap
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`h-full ${bgMain} overflow-auto pb-20`}>
      {/* Header */}
      <div className={`sticky top-0 z-40 ${bgCard} border-b ${borderColor}`}>
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Compass className="w-6 h-6 text-[#1e3a5f]" />
            <h1 className={`text-xl font-bold ${textPrimary}`}>Gezgin Topluluğu</h1>
          </div>
          <div className="flex items-center gap-2">
            <button 
              onClick={() => setShowSearchModal(true)}
              className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} transition-colors`}
            >
              <Search className={`w-5 h-5 ${textPrimary}`} />
            </button>
            <button 
              onClick={() => setShowNewPostModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#1e3a5f] text-white rounded-full text-sm font-medium hover:bg-[#2a4a73] transition-colors"
            >
              <Plus className="w-4 h-4" />
              Paylaş
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto">
        {/* Category Filter Bar */}
        <div className={`${bgCard} border-b ${borderColor} py-3`}>
          <div className="flex gap-2 px-4 overflow-x-auto pb-1 scrollbar-hide">
            <button 
              onClick={() => setActiveCategory('all')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                activeCategory === 'all' 
                  ? (darkMode ? 'bg-slate-700 text-white' : 'bg-gray-900 text-white')
                  : (darkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500')
              }`}
            >
              Tümü
            </button>
            {categories.map((cat) => {
              const Icon = cat.icon;
              const style = getCategoryStyle(cat.id);
              const isActive = activeCategory === cat.id;
              return (
                <button 
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    isActive 
                      ? `${style.bg} ${style.text} ring-2 ring-offset-1 ${style.border}`
                      : `${darkMode ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500'}`
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {cat.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${borderColor} ${bgCard}`}>
          <button
            onClick={() => setActiveTab('feed')}
            className={`flex-1 py-3 text-center font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'feed' 
                ? `${textPrimary} border-b-2 border-[#1e3a5f]` 
                : textSecondary
            }`}
          >
            <Navigation className="w-4 h-4" />
            Keşifler
          </button>
          <button
            onClick={() => setActiveTab('discover')}
            className={`flex-1 py-3 text-center font-medium transition-colors flex items-center justify-center gap-2 ${
              activeTab === 'discover' 
                ? `${textPrimary} border-b-2 border-[#1e3a5f]` 
                : textSecondary
            }`}
          >
            <TrendingUp className="w-4 h-4" />
            Trend
          </button>
        </div>

        {/* Feed Tab */}
        {activeTab === 'feed' && (
          <div>
            {/* Travel Posts */}
            {Array.isArray(filteredPosts) && filteredPosts.map((post) => {
              const CategoryIcon = getCategoryIcon(post.category);
              const categoryStyle = getCategoryStyle(post.category);
              const category = categories.find(c => c.id === post.category);
              
              return (
                <div key={post.id} className={`${bgCard} border-b ${borderColor}`}>
                  {/* Post Header */}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => openUserProfile(post.authorId, post.authorName)}
                        className="relative"
                      >
                        <div className={`w-12 h-12 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center font-bold text-lg ${textPrimary} hover:ring-2 ring-teal-500 transition-all overflow-hidden`}>
                          {post.authorAvatar ? (
                            <img src={post.authorAvatar} alt="" className="w-full h-full object-cover" />
                          ) : post.authorName?.charAt(0)}
                        </div>
                        {post.authorBadge && (
                          <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs ${
                            post.authorBadge === 'Yerel Rehber' ? 'bg-blue-500' :
                            post.authorBadge === 'Gurme' ? 'bg-orange-500' :
                            post.authorBadge === 'Fotoğrafçı' ? 'bg-purple-500' : 'bg-green-500'
                          } text-white`}>
                            {post.authorBadge === 'Yerel Rehber' ? '🧭' :
                             post.authorBadge === 'Gurme' ? '🍽️' :
                             post.authorBadge === 'Fotoğrafçı' ? '📸' : '🏔️'}
                          </div>
                        )}
                      </button>
                      <div>
                        <div className="flex items-center gap-2">
                          <button 
                            onClick={() => openUserProfile(post.authorId, post.authorName)}
                            className={`font-semibold text-sm ${textPrimary} hover:underline`}
                          >
                            {post.authorName}
                          </button>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${categoryStyle.bg} ${categoryStyle.text}`}>
                            {category?.label}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 text-xs">
                          <MapPin className={`w-3 h-3 ${textSecondary}`} />
                          <span className={textSecondary}>{post.placeName}</span>
                        </div>
                      </div>
                    </div>
                    <button className={`p-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-full`}>
                      <MoreHorizontal className={`w-5 h-5 ${textSecondary}`} />
                    </button>
                  </div>

                  {/* Post Image */}
                  {post.image && (
                    <div className="aspect-[4/3] bg-gray-100 relative">
                      <img 
                        src={post.image} 
                        alt="Post" 
                        className="w-full h-full object-cover"
                      />
                      {/* Rating Badge */}
                      {post.rating > 0 && (
                        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 rounded-full">
                          <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                          <span className="text-white text-sm font-medium">{post.rating}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Post Content */}
                  <div className="p-4">
                    <p className={`text-sm ${textPrimary} mb-3`}>{post.content}</p>

                    {/* Travel-Specific Actions */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        {/* Ben de Gittim */}
                        <button 
                          onClick={() => toggleVisited(post.id)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                            visitedPosts.includes(post.id)
                              ? 'bg-green-500 text-white'
                              : `${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary}`
                          }`}
                        >
                          <CheckCircle2 className="w-4 h-4" />
                          {visitedPosts.includes(post.id) ? 'Gittim' : 'Ben de Gittim'}
                        </button>
                        
                        {/* Rotama Ekle */}
                        <button 
                          onClick={() => handleAddToRoute(post)}
                          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary} transition-colors`}
                        >
                          <Route className="w-4 h-4" />
                          Rotama Ekle
                        </button>
                      </div>
                      
                      {/* Save */}
                      <button 
                        onClick={() => toggleSave(post.id)}
                        className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                      >
                        <Bookmark 
                          className={`w-5 h-5 ${savedPosts.includes(post.id) ? 'fill-[#1e3a5f] text-[#1e3a5f]' : textPrimary}`} 
                        />
                      </button>
                    </div>

                    {/* Stats */}
                    <div className={`flex items-center gap-4 text-sm ${textSecondary}`}>
                      <span className="flex items-center gap-1">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        {post.visited} kişi gitti
                      </span>
                      <button onClick={() => openComments(post.id)} className="flex items-center gap-1 hover:underline">
                        <MessageCircle className="w-4 h-4" />
                        {post.comments} yorum
                      </button>
                      <span className="flex items-center gap-1">
                        <Bookmark className="w-4 h-4" />
                        {post.saves} kayıt
                      </span>
                    </div>

                    {/* Timestamp */}
                    <p className={`text-xs ${textSecondary} mt-2`}>{post.timestamp}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Discover/Trend Tab */}
        {activeTab === 'discover' && (
          <div className="p-4 space-y-6">
            {/* Trending Places */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className={`font-bold ${textPrimary} flex items-center gap-2`}>
                  <TrendingUp className="w-5 h-5 text-orange-500" />
                  Bu Hafta Trend
                </h2>
              </div>
              <div className="space-y-3">
                {Array.isArray(trendingPlaces) && trendingPlaces.map((place, index) => (
                  <div key={place.id} className={`flex items-center gap-3 p-3 ${bgCard} rounded-xl`}>
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      index === 0 ? 'bg-yellow-500 text-white' :
                      index === 1 ? 'bg-gray-400 text-white' :
                      'bg-orange-400 text-white'
                    }`}>
                      {index + 1}
                    </span>
                    <img src={place.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    <div className="flex-1">
                      <p className={`font-medium ${textPrimary}`}>{place.name}</p>
                      <p className={`text-sm ${textSecondary}`}>{place.visits} ziyaret</p>
                    </div>
                    <span className="text-green-500 text-sm font-medium">{place.trend}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Local Guides */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <h2 className={`font-bold ${textPrimary} flex items-center gap-2`}>
                  <Award className="w-5 h-5 text-blue-500" />
                  Yerel Rehberler
                </h2>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {Array.isArray(localGuides) && localGuides.map((guide) => (
                  <div key={guide.id} className={`flex items-center gap-3 p-4 ${bgCard} rounded-xl`}>
                    <div 
                      className="relative cursor-pointer"
                      onClick={() => openUserProfile(guide.id, guide.name)}
                    >
                      <div className={`w-14 h-14 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center text-xl font-bold ${textPrimary}`}>
                        {guide.name.charAt(0)}
                      </div>
                      {guide.verified && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-3 h-3 text-white" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <p 
                        className={`font-semibold ${textPrimary} cursor-pointer hover:underline`}
                        onClick={() => openUserProfile(guide.id, guide.name)}
                      >
                        {guide.name}
                      </p>
                      <p className={`text-sm ${textSecondary}`}>{guide.badge} • {guide.visited} mekan keşfetti</p>
                    </div>
                    <button
                      onClick={() => handleFollow(guide.id)}
                      className={`px-4 py-2 rounded-lg font-medium text-sm ${
                        followingUsers.includes(guide.id)
                          ? `${darkMode ? 'bg-slate-700' : 'bg-gray-200'} ${textPrimary}`
                          : 'bg-[#1e3a5f] text-white'
                      }`}
                    >
                      {followingUsers.includes(guide.id) ? 'Takip' : 'Takip Et'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* New Post Modal */}
      {showNewPostModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className={`${bgCard} w-full max-w-lg rounded-2xl overflow-hidden max-h-[90vh] overflow-y-auto`}>
            <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
              <button onClick={closePostModal}>
                <X className={`w-6 h-6 ${textPrimary}`} />
              </button>
              <h2 className={`font-semibold ${textPrimary}`}>Keşfini Paylaş</h2>
              <button 
                onClick={handleCreatePost}
                disabled={!newPost.trim() || !selectedCategory || isUploading}
                className="text-[#1e3a5f] font-semibold disabled:opacity-50"
              >
                {isUploading ? 'Yükleniyor...' : 'Paylaş'}
              </button>
            </div>
            
            {/* Category Selection */}
            <div className={`p-4 border-b ${borderColor}`}>
              <p className={`text-sm font-medium ${textSecondary} mb-3`}>Ne paylaşıyorsun?</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const Icon = cat.icon;
                  const style = getCategoryStyle(cat.id);
                  const isSelected = selectedCategory === cat.id;
                  return (
                    <button 
                      key={cat.id}
                      onClick={() => setSelectedCategory(cat.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-all ${
                        isSelected 
                          ? `${style.bg} ${style.text} ring-2 ${style.border}`
                          : `${darkMode ? 'bg-slate-700' : 'bg-gray-100'} ${textSecondary}`
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      {cat.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="p-4">
              <textarea
                value={newPost}
                onChange={(e) => setNewPost(e.target.value)}
                placeholder="Deneyimini paylaş, ipucu ver..."
                className={`w-full bg-transparent ${textPrimary} resize-none outline-none text-sm`}
                rows={4}
              />
              
              {/* Place Selector */}
              <div className={`flex items-center gap-2 p-3 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-xl mt-3`}>
                <MapPin className="w-5 h-5 text-[#1e3a5f]" />
                <input 
                  type="text"
                  placeholder="Mekan ekle..."
                  className={`flex-1 bg-transparent ${textPrimary} outline-none text-sm`}
                />
              </div>

              {/* Rating */}
              <div className="flex items-center gap-3 mt-4">
                <span className={`text-sm ${textSecondary}`}>Puan:</span>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map((star) => (
                    <button key={star} onClick={() => setPostRating(star)}>
                      <Star className={`w-6 h-6 ${star <= postRating ? 'fill-yellow-400 text-yellow-400' : textSecondary}`} />
                    </button>
                  ))}
                </div>
              </div>

              {/* Tagged Users */}
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${textSecondary}`}>Etiketlenenler:</span>
                  <button
                    onClick={() => setShowUserTagModal(true)}
                    className="text-teal-500 text-sm font-medium hover:underline"
                  >
                    + Kişi Etiketle
                  </button>
                </div>
                {Array.isArray(taggedUsers) && taggedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {taggedUsers.map((user) => (
                      <div
                        key={user.id}
                        className={`flex items-center gap-2 px-3 py-1.5 ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} rounded-full`}
                      >
                        <span className={`text-sm ${textPrimary}`}>@{user.username || user.name}</span>
                        <button
                          onClick={() => removeTaggedUser(user.id)}
                          className={`${darkMode ? 'hover:bg-slate-600' : 'hover:bg-gray-200'} rounded-full p-0.5`}
                        >
                          <X className={`w-3 h-3 ${textSecondary}`} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Image Preview */}
              {postImagePreview && (
                <div className="mt-4 relative">
                  <img 
                    src={postImagePreview} 
                    alt="Seçilen fotoğraf" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <button 
                    onClick={removeImage}
                    className="absolute top-2 right-2 bg-black/50 p-1 rounded-full hover:bg-black/70 transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              )}
            </div>

            {/* Actions - Photo Upload Buttons */}
            <div className={`flex items-center gap-4 p-4 border-t ${borderColor}`}>
              {/* Hidden file inputs */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                className="hidden"
              />
              <input
                type="file"
                ref={cameraInputRef}
                onChange={handleCameraCapture}
                accept="image/*"
                capture="environment"
                className="hidden"
              />
              
              {/* Gallery button */}
              <button 
                onClick={() => fileInputRef.current?.click()}
                className={`p-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-lg transition-colors flex items-center gap-2`}
                title="Galeriden seç"
              >
                <Image className={`w-6 h-6 ${postImagePreview ? 'text-teal-500' : textSecondary}`} />
                <span className={`text-sm ${textSecondary}`}>Galeri</span>
              </button>
              
              {/* Camera button */}
              <button 
                onClick={() => cameraInputRef.current?.click()}
                className={`p-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-lg transition-colors flex items-center gap-2`}
                title="Fotoğraf çek"
              >
                <Camera className={`w-6 h-6 ${textSecondary}`} />
                <span className={`text-sm ${textSecondary}`}>Kamera</span>
              </button>

              {/* Loading indicator */}
              {isUploading && (
                <div className="ml-auto flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className={`text-sm ${textSecondary}`}>Yükleniyor...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comments Modal */}
      {showComments && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
          <div className={`${bgCard} w-full max-w-lg rounded-t-2xl sm:rounded-2xl max-h-[70vh] flex flex-col`}>
            <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
              <h2 className={`font-semibold ${textPrimary}`}>Yorumlar</h2>
              <button onClick={() => setShowComments(null)}>
                <X className={`w-6 h-6 ${textPrimary}`} />
              </button>
            </div>
            <div className="flex-1 overflow-auto p-4 space-y-4">
              {Array.isArray(comments) && comments.map((comment) => (
                <div key={comment.id} className="flex gap-3">
                  <div className={`w-8 h-8 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center text-sm font-bold ${textPrimary}`}>
                    {comment.authorName?.charAt(0)}
                  </div>
                  <div className="flex-1">
                    <p className={`text-sm ${textPrimary}`}>
                      <span className="font-semibold">{comment.authorName}</span>{' '}
                      {comment.content}
                    </p>
                    <p className={`text-xs ${textSecondary} mt-1`}>{comment.timestamp}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className={`p-4 border-t ${borderColor} flex gap-3`}>
              <input
                type="text"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Yorum ekle..."
                className={`flex-1 bg-transparent ${textPrimary} outline-none`}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment()}
              />
              <button 
                onClick={handleAddComment}
                disabled={!newComment.trim()}
                className="text-[#1e3a5f] font-semibold disabled:opacity-50"
              >
                Gönder
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Tag Modal */}
      {showUserTagModal && (
        <div 
          className="fixed inset-0 bg-black/60 z-[60] flex items-start justify-center pt-20"
          onClick={() => setShowUserTagModal(false)}
        >
          <div 
            className={`${bgCard} w-full max-w-lg rounded-2xl overflow-hidden shadow-xl mx-4`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Header */}
            <div className={`p-4 border-b ${borderColor}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className={`font-semibold ${textPrimary}`}>Kişi Etiketle</h2>
                <button onClick={() => setShowUserTagModal(false)}>
                  <X className={`w-5 h-5 ${textPrimary}`} />
                </button>
              </div>
              <div className="flex items-center gap-3">
                <Users className={`w-5 h-5 ${textSecondary}`} />
                <input
                  type="text"
                  value={userTagSearch}
                  onChange={(e) => handleUserTagSearch(e.target.value)}
                  placeholder="Kullanıcı ara..."
                  className={`flex-1 bg-transparent outline-none ${textPrimary} placeholder:${textSecondary}`}
                  autoFocus
                />
                {userTagSearch && (
                  <button onClick={() => { setUserTagSearch(''); setUserTagResults([]); }}>
                    <X className={`w-5 h-5 ${textSecondary}`} />
                  </button>
                )}
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-96 overflow-y-auto">
              {Array.isArray(userTagResults) && userTagResults.length > 0 ? (
                userTagResults.map((user) => (
                  <button
                    key={user.id}
                    onClick={() => addTaggedUser(user)}
                    className={`w-full flex items-center gap-3 p-4 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    <div className={`w-12 h-12 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center overflow-hidden`}>
                      {user.avatar ? (
                        <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className={`font-bold text-lg ${textPrimary}`}>{user.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${textPrimary}`}>{user.name}</span>
                        <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      </div>
                      {user.username && (
                        <p className={`text-sm ${textSecondary}`}>@{user.username}</p>
                      )}
                    </div>
                  </button>
                ))
              ) : userTagSearch ? (
                <div className="text-center py-8">
                  <p className={textSecondary}>Kullanıcı bulunamadı</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Users className={`w-12 h-12 mx-auto mb-2 ${textSecondary}`} />
                  <p className={textSecondary}>Etiketlemek için kullanıcı ara</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* User Profile Modal - Instagram Style */}
      {selectedUser && (
        <div 
          className={`fixed inset-0 ${bgMain} z-50 overflow-hidden`}
        >
          <div 
            className={`${bgMain} w-full h-full overflow-hidden flex flex-col`}
          >
            {/* Cover Image Section */}
            <div className="relative max-w-3xl mx-auto w-full">
              {/* Cover Image */}
              <div className="h-48 bg-gradient-to-br from-orange-400 via-rose-300 to-purple-400 relative overflow-hidden">
                {selectedUser.coverImage ? (
                  <img src={selectedUser.coverImage} alt="Cover" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-orange-400 via-rose-300 to-purple-400 flex items-center justify-center">
                    <div className="text-white/60 flex flex-col items-center">
                      <Camera className="w-8 h-8 mb-1" />
                      <span className="text-sm">Kapak Fotoğrafı</span>
                    </div>
                  </div>
                )}
                {/* Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-black/20" />
              </div>

              {/* Top Navigation Bar - Overlay on Cover */}
              <div className="absolute top-0 left-0 right-0 z-10 px-4 py-3 flex items-center justify-between">
                <button 
                  onClick={() => { setSelectedUser(null); setSelectedUserPost(null); }}
                  className="p-2 bg-black/30 hover:bg-black/50 rounded-full backdrop-blur-sm"
                >
                  <ChevronLeft className="w-5 h-5 text-white" />
                </button>
                <div className="w-9" />
              </div>

              {/* Profile Info Card - Overlapping Cover */}
              <div className={`relative mx-4 -mt-16 ${bgCard} rounded-2xl shadow-lg p-4`}>
                <div className="flex items-start gap-4">
                  {/* Profile Photo */}
                  <div className="relative -mt-12">
                    <div className="w-24 h-24 rounded-2xl overflow-hidden ring-4 ring-white dark:ring-slate-800 shadow-lg">
                      {selectedUser.avatar ? (
                        <img src={selectedUser.avatar} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] flex items-center justify-center text-white text-2xl font-bold">
                          {selectedUser.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Name, Username and Verified Badge */}
                  <div className="flex-1 pt-2">
                    <div className="flex items-center gap-2">
                      <h1 className={`text-lg font-bold ${textPrimary}`}>
                        {selectedUser.name || 'Kullanıcı'}
                      </h1>
                      <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    </div>
                    {/* Username */}
                    {selectedUser.username && (
                      <p className={`text-sm ${darkMode ? 'text-slate-400' : 'text-gray-500'} mt-0.5`}>
                        @{selectedUser.username}
                      </p>
                    )}
                    
                    {/* Stats Row */}
                    <div className="flex items-center gap-4 mt-3">
                      <div className="text-center">
                        <p className={`text-lg font-bold ${textPrimary}`}>{selectedUserStats.followers}</p>
                        <p className={`text-xs ${textSecondary}`}>Takipçi</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-lg font-bold ${textPrimary}`}>{selectedUserStats.posts}</p>
                        <p className={`text-xs ${textSecondary}`}>Rota</p>
                      </div>
                      <div className="text-center">
                        <p className={`text-lg font-bold ${textPrimary}`}>{selectedUserStats.following}</p>
                        <p className={`text-xs ${textSecondary}`}>Mekan</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bio */}
                <div className="mt-4">
                  <p className={`text-sm ${textPrimary}`}>
                    {selectedUser.bio || '🌍 Gezgin • Yeni yerler keşfetmeyi seviyorum ✨'}
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2 mt-4">
                  {selectedUser.id === user?.id ? (
                    <button
                      onClick={() => { setSelectedUser(null); setSelectedUserPost(null); if (onTabChange) onTabChange('profile'); }}
                      className="flex-1 py-2.5 px-4 bg-teal-500 hover:bg-teal-600 text-white rounded-xl font-medium transition-colors"
                    >
                      Profili Düzenle
                    </button>
                  ) : (
                    <>
                      <button 
                        onClick={handleProfileFollow}
                        className={`flex-1 py-2.5 px-4 rounded-xl font-medium transition-colors ${
                          isFollowing 
                            ? (darkMode ? 'bg-slate-700 text-white' : 'bg-gray-200 text-gray-900')
                            : 'bg-teal-500 hover:bg-teal-600 text-white'
                        }`}
                      >
                        {isFollowing ? 'Takip Ediliyor' : 'Takip Et'}
                      </button>
                      <button 
                        onClick={(e) => { e.stopPropagation(); openMessageModal(selectedUser.id, selectedUser.name); }}
                        className={`py-2.5 px-4 rounded-xl font-medium ${darkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-900'}`}
                      >
                        <MessageSquare className="w-5 h-5" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {userProfileLoading ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto" onClick={(e) => e.stopPropagation()}>

                {/* Filters - Community Style */}
                <div className={`border-t border-b ${borderColor} mt-4 max-w-3xl mx-auto`}>
                  <div className="flex gap-2 overflow-x-auto px-4 py-3 scrollbar-hide">
                    {[{ id: 'all', icon: Grid3X3, label: 'Tümü', color: 'slate' }, ...categories].map((cat) => {
                      const isActive = userProfileFilter === cat.id;
                      const style = cat.id === 'all'
                        ? { bg: darkMode ? 'bg-slate-700' : 'bg-gray-100', text: darkMode ? 'text-white' : 'text-gray-900', border: darkMode ? 'border-slate-600' : 'border-gray-200' }
                        : getCategoryStyle(cat.id);
                      const Icon = cat.icon;
                      return (
                        <button
                          key={cat.id}
                          onClick={(e) => { e.stopPropagation(); setUserProfileFilter(cat.id); }}
                          className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap transition-all ${
                            isActive
                              ? `${style.bg} ${style.text} ${cat.id === 'all' ? '' : style.border} border-2 scale-105`
                              : `${darkMode ? 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`
                          }`}
                        >
                          <Icon className={`w-4 h-4 ${isActive ? style.text : ''}`} />
                          <span>{cat.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Posts Content - Grid Only */}
                <div className="flex-1 overflow-y-auto max-w-3xl mx-auto w-full">
                  {getFilteredUserPosts().length > 0 ? (
                    <div className="grid grid-cols-3 gap-0.5">
                        {getFilteredUserPosts().map((post, index) => (
                          <div 
                            key={post.id} 
                            className="aspect-square relative group cursor-pointer"
                            onClick={() => { setSelectedUserPost(post); setCurrentUserPostIndex(index); }}
                          >
                            {post.image ? (
                              <img src={post.image} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <div className={`w-full h-full ${darkMode ? 'bg-slate-700' : 'bg-gray-100'} flex items-center justify-center p-2`}>
                                <p className={`text-xs ${textSecondary} text-center line-clamp-3`}>{post.content}</p>
                              </div>
                            )}
                            {/* Category badge */}
                            {post.category && (
                              <div className="absolute top-1 left-1">
                                <span className={`text-xs px-1.5 py-0.5 rounded ${
                                  post.category === 'visit' ? 'bg-blue-500' :
                                  post.category === 'food' ? 'bg-orange-500' :
                                  post.category === 'nature' ? 'bg-green-500' :
                                  post.category === 'photo' ? 'bg-purple-500' : 'bg-yellow-500'
                                } text-white`}>
                                  {post.category === 'visit' ? '📍' :
                                   post.category === 'food' ? '🍽️' :
                                   post.category === 'nature' ? '🏔️' :
                                   post.category === 'photo' ? '📸' : '💡'}
                                </span>
                              </div>
                            )}
                            {/* Hover overlay */}
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                              <div className="flex items-center gap-1 text-white text-sm">
                                <Heart className="w-4 h-4 fill-white" />
                                <span>{post.likes || 0}</span>
                              </div>
                              <div className="flex items-center gap-1 text-white text-sm">
                                <MessageCircle className="w-4 h-4 fill-white" />
                                <span>{post.comments || 0}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                  ) : (
                    <div className="text-center py-12">
                      <Camera className={`w-12 h-12 mx-auto mb-3 ${textSecondary}`} />
                      <p className={textSecondary}>
                        {userProfileFilter === 'all' ? 'Henüz gönderi yok' : 'Bu kategoride gönderi yok'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Post Detail View - Instagram Style Full Page Feed */}
          {selectedUserPost && (
            <div className={`fixed inset-0 ${bgMain} z-[60] overflow-hidden`}>
              {/* Header - Instagram Style with Back Arrow */}
              <div className={`${bgCard} sticky top-0 z-10 border-b ${borderColor}`}>
                <div className="max-w-3xl mx-auto flex items-center px-4 py-3">
                  <button 
                    onClick={() => setSelectedUserPost(null)}
                    className={`p-2 -ml-2 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'} rounded-full`}
                  >
                    <ChevronLeft className={`w-6 h-6 ${textPrimary}`} />
                  </button>
                  <h1 className={`flex-1 text-center text-lg font-semibold ${textPrimary}`}>Gönderiler</h1>
                  <div className="w-10" />
                </div>
              </div>

              {/* Scrollable Feed - Community Page Style */}
              <div className="h-[calc(100vh-56px)] overflow-y-auto pb-20">
                <div className="max-w-3xl mx-auto w-full">
                {getFilteredUserPosts().map((post) => {
                  const isVisited = visitedPosts.includes(post.id);
                  const isSaved = savedPosts.includes(post.id);
                  return (
                  <div key={post.id} className={`${bgCard} border-b ${borderColor}`}>
                    {/* Post Header */}
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center font-bold text-lg ${textPrimary}`}>
                          {selectedUser.avatar ? (
                            <img src={selectedUser.avatar} alt="" className="w-full h-full object-cover rounded-full" />
                          ) : selectedUser.name?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className={`font-semibold text-sm ${textPrimary}`}>{selectedUser.name || 'Kullanıcı'}</span>
                            {post.category && (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${
                                post.category === 'visit' ? 'bg-blue-100 text-blue-600' :
                                post.category === 'food' ? 'bg-orange-100 text-orange-600' :
                                post.category === 'nature' ? 'bg-green-100 text-green-600' :
                                post.category === 'photo' ? 'bg-purple-100 text-purple-600' :
                                'bg-yellow-100 text-yellow-600'
                              }`}>
                                {post.category === 'visit' ? 'Mekan Ziyareti' :
                                 post.category === 'food' ? 'Yemek Deneyimi' :
                                 post.category === 'nature' ? 'Doğa Keşfi' :
                                 post.category === 'photo' ? 'Fotoğraf Noktası' : 'Yerel İpucu'}
                              </span>
                            )}
                          </div>
                          {post.placeName && (
                            <div className="flex items-center gap-1 text-xs">
                              <MapPin className={`w-3 h-3 ${textSecondary}`} />
                              <span className={textSecondary}>{post.placeName}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Post Image */}
                    {post.image && (
                      <div className="aspect-[4/3] bg-gray-100 relative">
                        <img src={post.image} alt="Post" className="w-full h-full object-cover" />
                        {post.rating > 0 && (
                          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-black/60 rounded-full">
                            <Star className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                            <span className="text-white text-sm font-medium">{post.rating}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Post Content */}
                    <div className="p-4">
                      <p className={`text-sm ${textPrimary} mb-3`}>{post.content}</p>

                      {/* Travel-Specific Actions */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <button 
                            onClick={() => toggleVisited(post.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                              isVisited
                                ? 'bg-green-500 text-white'
                                : `${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary}`
                            }`}
                          >
                            <CheckCircle2 className="w-4 h-4" />
                            {isVisited ? 'Gittim' : 'Ben de Gittim'}
                          </button>
                          <button 
                            onClick={() => handleAddToRoute(post)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-100 hover:bg-gray-200'} ${textPrimary} transition-colors`}
                          >
                            <Route className="w-4 h-4" />
                            Rotama Ekle
                          </button>
                        </div>
                        <button 
                          onClick={() => toggleSave(post.id)}
                          className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
                        >
                          <Bookmark className={`w-5 h-5 ${isSaved ? 'fill-[#1e3a5f] text-[#1e3a5f]' : textPrimary}`} />
                        </button>
                      </div>

                      {/* Stats */}
                      <div className={`flex items-center gap-4 text-sm ${textSecondary}`}>
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {post.visited || 0} kişi gitti
                        </span>
                        <button onClick={() => openComments(post.id)} className="flex items-center gap-1 hover:underline">
                          <MessageCircle className="w-4 h-4" />
                          {post.comments || 0} yorum
                        </button>
                        <span className="flex items-center gap-1">
                          <Bookmark className="w-4 h-4" />
                          {post.saves || 0} kayıt
                        </span>
                      </div>

                      {/* Timestamp */}
                      <p className={`text-xs ${textSecondary} mt-2`}>{post.timestamp || 'Az önce'}</p>
                    </div>
                  </div>
                  );
                })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* User Search Modal */}
      {showSearchModal && (
        <div 
          className="fixed inset-0 bg-black/60 z-[70] flex items-start justify-center pt-20"
          onClick={() => setShowSearchModal(false)}
        >
          <div 
            className={`${bgCard} w-full max-w-lg rounded-2xl overflow-hidden shadow-xl mx-4`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Search Header */}
            <div className={`p-4 border-b ${borderColor}`}>
              <div className="flex items-center gap-3">
                <Search className={`w-5 h-5 ${textSecondary}`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Kullanıcı ara..."
                  className={`flex-1 bg-transparent outline-none ${textPrimary} placeholder:${textSecondary}`}
                  autoFocus
                />
                {searchQuery && (
                  <button onClick={() => { setSearchQuery(''); setSearchResults([]); }}>
                    <X className={`w-5 h-5 ${textSecondary}`} />
                  </button>
                )}
              </div>
            </div>

            {/* Search Results */}
            <div className="max-h-96 overflow-y-auto">
              {searchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : Array.isArray(searchResults) && searchResults.length > 0 ? (
                searchResults.map((result) => (
                  <button
                    key={result.id}
                    onClick={() => {
                      setShowSearchModal(false);
                      setSearchQuery('');
                      setSearchResults([]);
                      openUserProfile(result.id, result.name);
                    }}
                    className={`w-full flex items-center gap-3 p-4 ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-50'} transition-colors`}
                  >
                    <div className={`w-12 h-12 rounded-full ${darkMode ? 'bg-slate-700' : 'bg-gray-200'} flex items-center justify-center overflow-hidden`}>
                      {result.avatar ? (
                        <img src={result.avatar} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <span className={`font-bold text-lg ${textPrimary}`}>{result.name?.charAt(0)}</span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold ${textPrimary}`}>{result.name}</span>
                        <div className="w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                          <Check className="w-2.5 h-2.5 text-white" />
                        </div>
                      </div>
                      {result.username && (
                        <p className={`text-sm ${textSecondary}`}>@{result.username}</p>
                      )}
                      {result.bio && (
                        <p className={`text-xs ${textSecondary} line-clamp-1`}>{result.bio}</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className={`text-sm font-medium ${textPrimary}`}>{result.followers || 0}</p>
                      <p className={`text-xs ${textSecondary}`}>takipçi</p>
                    </div>
                  </button>
                ))
              ) : searchQuery.length >= 2 ? (
                <div className="text-center py-8">
                  <UserCircle className={`w-12 h-12 mx-auto mb-2 ${textSecondary}`} />
                  <p className={textSecondary}>Kullanıcı bulunamadı</p>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Search className={`w-12 h-12 mx-auto mb-2 ${textSecondary}`} />
                  <p className={textSecondary}>Kullanıcı aramak için yazın</p>
                  <p className={`text-xs ${textSecondary} mt-1`}>En az 2 karakter</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Message Modal */}
      {showMessageModal && messageRecipient && (
        <div 
          className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
          onClick={() => setShowMessageModal(false)}
        >
          <div 
            className={`w-full max-w-md ${bgCard} rounded-2xl shadow-xl overflow-hidden max-h-[80vh] flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#1e3a5f] to-[#0d9488] flex items-center justify-center">
                  <span className="text-white font-bold">{messageRecipient.name?.charAt(0) || 'K'}</span>
                </div>
                <div>
                  <h3 className={`font-semibold ${textPrimary}`}>{messageRecipient.name}</h3>
                  <p className={`text-xs ${textSecondary}`}>Aktif</p>
                </div>
              </div>
              <button 
                onClick={() => setShowMessageModal(false)}
                className={`p-2 rounded-full ${darkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
              >
                <X className={`w-5 h-5 ${textPrimary}`} />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[300px]">
              {Array.isArray(messages) && messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <MessageCircle className={`w-12 h-12 ${textSecondary} mb-3`} />
                  <p className={`${textSecondary}`}>Henüz mesaj yok</p>
                  <p className={`text-sm ${textSecondary}`}>İlk mesajınızı gönderin!</p>
                </div>
              ) : (
                Array.isArray(messages) && messages.map((msg, idx) => {
                  const isOwnMessage = msg.senderId === user?.id;
                  const isLastOwnMessage = isOwnMessage && 
                    messages.slice(idx + 1).every(m => m.senderId !== user?.id);
                  
                  return (
                    <div 
                      key={msg.id || idx}
                      className={`flex flex-col ${isOwnMessage ? 'items-end' : 'items-start'}`}
                    >
                      <div 
                        className={`max-w-[80%] p-3 rounded-2xl ${
                          isOwnMessage 
                            ? 'bg-teal-500 text-white rounded-br-md' 
                            : (darkMode ? 'bg-slate-700 text-white' : 'bg-gray-100 text-gray-900') + ' rounded-bl-md'
                        }`}
                      >
                        <p className="text-sm">{msg.content}</p>
                        <p className={`text-xs mt-1 ${isOwnMessage ? 'text-teal-100' : textSecondary}`}>
                          {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </div>
                      {/* Görüldü indicator - only show on last own message if read */}
                      {isOwnMessage && isLastOwnMessage && msg.read && (
                        <div className={`flex items-center gap-1 mt-1 mr-1`}>
                          <CheckCircle2 className="w-3 h-3 text-teal-500" />
                          <span className={`text-xs ${textSecondary}`}>Görüldü</span>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            {/* Input */}
            <div className={`p-4 border-t ${borderColor}`}>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Mesajınızı yazın..."
                  className={`flex-1 px-4 py-3 rounded-full ${darkMode ? 'bg-slate-700 text-white placeholder-slate-400' : 'bg-gray-100 text-gray-900 placeholder-gray-400'} outline-none`}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={!newMessage.trim() || sendingMessage}
                  className={`p-3 rounded-full transition-colors ${
                    newMessage.trim() && !sendingMessage
                      ? 'bg-teal-500 text-white hover:bg-teal-600'
                      : (darkMode ? 'bg-slate-700 text-slate-500' : 'bg-gray-200 text-gray-400')
                  }`}
                >
                  <Send className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommunityPage;